import { useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    TextField,
    Button,
    CircularProgress,
    Chip,
    Alert,
    Tooltip,
} from '@mui/material'
import { Close, AutoAwesome, Person, Star, OpenInNew } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import {
    useAdminRecommendationsApi,
    RecommendedProduct,
} from './useAdminRecommendationsApi'
import { ProductUtils } from 'wakacje-web-legacy/src/utils/ProductUtils'

interface BookingRecommendationsDialogProps {
    open: boolean
    onClose: () => void
    bookingData: any
}

const RecommendationCard = ({ product }: { product: RecommendedProduct }) => {
    const photoUrl = product.main_photo?.url
        ? product.main_photo.url
        : null

    const priceFormatted = product.min_price
        ? ProductUtils.formatPriceInCents(product.min_price, false, 'zł')
        : null

    const tripPriceFormatted = product.nearest_trip?.full_price
        ? ProductUtils.formatPriceInCents(product.nearest_trip.full_price, false, 'zł')
        : null

    const productUrl = `https://www.gofunlo.com/p/${product.slug || product.id}`

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white">
            {/* Photo */}
            <div className="h-36 bg-gray-100 relative">
                {photoUrl ? (
                    <img
                        src={photoUrl}
                        alt={product.name}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                        Brak zdjęcia
                    </div>
                )}
                {/* Score badge */}
                <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                    <Star fontSize="inherit" />
                    {product.ai_match_score}%
                </div>
            </div>

            {/* Content */}
            <div className="p-3">
                <h4 className="font-semibold text-sm text-gray-900 line-clamp-2 mb-1">
                    {product.name}
                </h4>

                <div className="flex flex-wrap gap-1 mb-2">
                    <Chip
                        label={ProductUtils.getTypeName(product.type)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                    {product.location_name && (
                        <Chip
                            label={product.location_name}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                    )}
                    {product.min_age > 0 && (
                        <Chip
                            label={`${product.min_age}-${product.max_age} lat`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                    )}
                </div>

                {/* Price */}
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-gray-500">
                        {product.company_name}
                    </div>
                    <div className="text-sm font-bold text-blue-700">
                        {tripPriceFormatted
                            ? `od ${tripPriceFormatted}`
                            : priceFormatted
                                ? `od ${priceFormatted}`
                                : ''}
                    </div>
                </div>

                {/* Nearest trip */}
                {product.nearest_trip && (
                    <div className="text-xs text-gray-500 mb-2">
                        Najbliższy termin:{' '}
                        <span className="font-medium text-gray-700">
                            {product.nearest_trip.start_date} — {product.nearest_trip.end_date}
                        </span>
                        {product.nearest_trip.available_spots > 0 && (
                            <span className="ml-1 text-green-600">
                                ({product.nearest_trip.available_spots} miejsc)
                            </span>
                        )}
                    </div>
                )}

                {/* AI reason */}
                <div className="bg-blue-50 border border-blue-100 rounded p-2 text-xs text-blue-800 flex gap-1.5 items-start">
                    <AutoAwesome sx={{ fontSize: 14, mt: '1px', flexShrink: 0 }} />
                    <span>{product.ai_match_reason}</span>
                </div>

                {/* Open link */}
                <a
                    href={productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                    Otwórz ofertę <OpenInNew sx={{ fontSize: 12 }} />
                </a>
            </div>
        </div>
    )
}

export const BookingRecommendationsDialog = ({
    open,
    onClose,
    bookingData,
}: BookingRecommendationsDialogProps) => {
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

    const { searchByBooking, loading, error, data, reset } =
        useAdminRecommendationsApi()

    const [additionalContext, setAdditionalContext] = useState('')

    const handleSearch = async () => {
        if (!bookingData?.id) return
        await searchByBooking(bookingData.id, additionalContext)
    }

    const handleClose = () => {
        reset()
        setAdditionalContext('')
        onClose()
    }

    const parentName = bookingData?.parent
        ? `${bookingData.parent.first_name ?? ''} ${bookingData.parent.last_name ?? ''}`.trim()
        : bookingData?.user_name ?? '—'

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={fullScreen}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { minHeight: '60vh' },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                }}
            >
                <div className="flex items-center gap-2">
                    <AutoAwesome sx={{ color: '#2563eb' }} />
                    <span className="font-bold">
                        AI Rekomendacje dla rezerwacji
                    </span>
                </div>
                <IconButton onClick={handleClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Booking summary */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                        <Person sx={{ fontSize: 16 }} />
                        Kontekst rezerwacji
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div>
                            <span className="text-gray-500">Rodzic:</span>{' '}
                            <span className="font-medium">{parentName}</span>
                        </div>
                        <div>
                            <span className="text-gray-500">Produkt:</span>{' '}
                            <span className="font-medium">
                                {bookingData?.product_name ?? '—'}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-500">Firma:</span>{' '}
                            <span className="font-medium">
                                {bookingData?.company_name ?? '—'}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-500">Status:</span>{' '}
                            <span className="font-medium">
                                {bookingData?.status ?? '—'}
                            </span>
                        </div>
                    </div>

                    {/* User context from AI response */}
                    {data?.booking_context && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs text-gray-500 mb-1">
                                Dane wyciągnięte z profilu użytkownika:
                            </div>
                            <div className="flex flex-wrap gap-2 text-xs">
                                {data.booking_context.kids.map((kid, i) => (
                                    <Chip
                                        key={i}
                                        label={`${kid.first_name} (${kid.age} lat)`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                    />
                                ))}
                                <Chip
                                    label={`${data.booking_context.past_bookings_count} wcześniejszych rezerwacji`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Additional context input */}
                <div className="mb-4">
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        label="Dodatkowy kontekst (opcjonalnie)"
                        placeholder='np. "rodzic wspomniał że córka interesuje się tańcem i sztuką"'
                        value={additionalContext}
                        onChange={(e) => setAdditionalContext(e.target.value)}
                        disabled={loading}
                        inputProps={{ maxLength: 1000 }}
                    />
                </div>

                {/* Search button */}
                <div className="mb-4">
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={loading || !bookingData?.id}
                        startIcon={
                            loading ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <AutoAwesome />
                            )
                        }
                        sx={{
                            backgroundColor: '#2563eb',
                            '&:hover': { backgroundColor: '#1d4ed8' },
                        }}
                    >
                        {loading
                            ? 'Szukam rekomendacji...'
                            : data
                                ? 'Szukaj ponownie'
                                : 'Znajdź rekomendacje'}
                    </Button>
                </div>

                {/* Error */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Results */}
                {data && !loading && (
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-gray-700">
                                Polecane oferty ({data.recommendations.length})
                            </h4>
                            <span className="text-xs text-gray-400">
                                z {data.total_candidates} kandydatów
                            </span>
                        </div>

                        {data.recommendations.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 text-sm">
                                Nie znaleziono pasujących ofert. Spróbuj dodać
                                więcej kontekstu.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {data.recommendations.map((product) => (
                                    <RecommendationCard
                                        key={product.id}
                                        product={product}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Initial state */}
                {!data && !loading && !error && (
                    <div className="text-center py-12 text-gray-400 text-sm">
                        <AutoAwesome
                            sx={{ fontSize: 48, mb: 1, opacity: 0.3 }}
                        />
                        <p>
                            Kliknij &quot;Znajdź rekomendacje&quot; aby AI
                            przeanalizowało profil użytkownika i zaproponowało
                            najlepsze oferty.
                        </p>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
